import { EmptyState } from '@synoptic/ui-kit/empty-state/empty-state.tsx';
import { TrashIcon } from '@synoptic/ui-kit/icons/react/trash.tsx';
import clsx from 'clsx';
import {
  PostCardFragmentFragment,
  RepostCardFragmentFragment,
} from '@/__generated__/graphql.ts';
import { Link } from '@/routes-utils/navigation.tsx';
import { paths } from '@/routes-utils/paths.ts';
import { formatDateWithMs } from '../../date-utils/format-date-with-ms.ts';
import { formatDate } from '../../date-utils/format-date.ts';
import {
  DEFAULT_COLLAPSED_BODY_CHARS,
  isStreamDeckSettingEnabled,
  StreamDeckSettings,
} from '../stream-deck/customize-stream-deck-dropdown/use-stream-deck-settings.ts';
import { type HighlightPattern } from '../text-editor/plugins/highlight/types';
import { useHighlightMatchSummary } from '../text-editor/plugins/highlight/use-highlight-match-summary';
import { TextEditor } from '../text-editor/text-editor.tsx';
import { PostCardMedia } from './post-card-media/post-card-media.tsx';
import { PostCardPreview } from './post-card-preview/post-card-preview.tsx';
import {
  postCardBody,
  postCardBodyWithDisabledLinks,
  postCardContent,
  postCardDate,
  postCardDeletedPlaceholder,
} from './post-card.css.ts';

export const PostCardContent = ({
  post,
  size = 'medium',
  streamDeckSettings,
  highlightPatterns,
}: {
  post: PostCardFragmentFragment | RepostCardFragmentFragment;
  size?: 'small' | 'medium';
  streamDeckSettings?: StreamDeckSettings;
  highlightPatterns?: HighlightPattern[];
}) => {
  const postContent =
    (post as RepostCardFragmentFragment)?.originalPost || post;

  const quotedPost =
    (postContent.quotedPost as RepostCardFragmentFragment)?.originalPost ||
    postContent.quotedPost;

  const arePostInteractionsEnabled = isStreamDeckSettingEnabled(
    'postInteractions',
    streamDeckSettings,
  );

  const areLinksEnabled = isStreamDeckSettingEnabled(
    'enabledLinks',
    streamDeckSettings,
  );

  const isCreatedDateEnabled = isStreamDeckSettingEnabled(
    'createdDate',
    streamDeckSettings,
  );

  const isDeleted = Boolean(postContent.deletedAt);

  const compactMaxChars = streamDeckSettings
    ? (streamDeckSettings.maxBodyChars ?? undefined)
    : DEFAULT_COLLAPSED_BODY_CHARS;
  const shouldCollapseQuoted =
    !!streamDeckSettings && streamDeckSettings.collapseQuotedPosts;

  const { inPost: matchesInPost, inHiddenPost: matchesInHiddenPost } =
    useHighlightMatchSummary({
      postText: postContent.text,
      patterns: highlightPatterns,
      maxChars: compactMaxChars,
    });

  return (
    <div className={postCardContent}>
      {isDeleted ? (
        <EmptyState
          illustration={<TrashIcon />}
          heading="The post was deleted"
          className={postCardDeletedPlaceholder}
        />
      ) : (
        <>
          <TextEditor
            namespace={
              arePostInteractionsEnabled === false
                ? `post:${postContent.id}-disabled-post-interactions`
                : `post:${postContent.id}`
            }
            className={clsx(postCardBody, {
              [postCardBodyWithDisabledLinks]: areLinksEnabled === false,
            })}
            initialState={postContent.json || postContent.text}
            editable={false}
            rich
            theme={size === 'small' ? 'small' : 'base'}
            highlightPatterns={highlightPatterns}
            collapseQuotes={shouldCollapseQuoted}
            forceExpandQuotes={matchesInPost}
            charLimit={compactMaxChars}
            withShowMore={!!compactMaxChars}
            forceExpand={matchesInHiddenPost}
          />

          <PostCardMedia
            media={postContent.media}
            authorId={postContent.author.id}
            createdAt={postContent.createdAt}
          />
        </>
      )}

      {quotedPost?.id && (
        <Link
          to={paths.makePost(quotedPost.id)}
          onClick={(e) => e.stopPropagation()}
        >
          <PostCardPreview
            post={quotedPost}
            hoverable={true}
            clickableAuthor={false}
          />
        </Link>
      )}

      {isCreatedDateEnabled === true && (
        <time
          title={formatDateWithMs(new Date(postContent.createdAt))}
          dateTime={postContent.createdAt}
          className={postCardDate}
        >
          {formatDate(new Date(postContent.createdAt))}
        </time>
      )}
    </div>
  );
};
